import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useQuery, useLazyQuery } from '@apollo/client';
import { ChevronRight } from 'react-feather';

import { nextAvailableTrainShape } from 'constants/propTypes';
import { GET_ANIMALS_PER_TRAINS, GET_SEATING_DATA, GET_ASSISTANCE_PER_TRAINS } from 'store/queries';
import { inventoryClassTypes } from 'types/booking';
import { getAllowedInventoryClasses, getClassOfServiceMapNameByInventoryClass, hasSameInventoryClass } from 'utils/inventoryClass';
import { CARRIAGE_BLOCKED_REASONS } from 'constants/seating';
import { types } from 'types/passengers';
import classOfService from 'constants/propTypes/classOfService';
import { Row, Column } from 'components/shared/Layout';
import { Spinner } from 'admin-components/components';

import AnimalsIcon from './AnimalsIcon';
import AssistanceIcon from './AssistanceIcon';
import {
  StyledTrainMap,
  StyledCarriageFeatures,
  StyledCarriageNumber,
  StyledCarriageBlockedTooltip,
  StyledNumberOfAvailableSeats,
  StyledCarriageClass,
  StyledCarriageWheelchairIcon,
  CarriageWrapper,
  Carriage,
  StyledAnimalsNumber,
  StyledAssistanceNumber
} from './TrainMap.styles';

const TrainMap = ({
  className,
  journey: {
    trainNumber,
    destination,
    origin,
    timing: { date },
    classOfService: { code, showNewTrainMapName },
    products
  },
  selectCarriage,
  currentCoach,
  nextAvailableTrain,
  shouldFetchAssistancesPerCoaches = false,
  currentInventoryClasses = []
}) => {
  const { loading, error, data } = useQuery(GET_SEATING_DATA, {
    variables: {
      serviceId: nextAvailableTrain?.trainNumber || trainNumber,
      destination: destination.uic,
      origin: origin.uic,
      travelDate: date
    },
    onCompleted: ({ getSeatingData }) => {
      const carriage = getSeatingData.carriages.find((c) => c.carriageNumber === currentCoach.toString());

      if (!carriage) {
        const availableCarriages = getSeatingData.carriages.filter((c) =>
          c.inventoryClasses.some((i) => currentInventoryClasses.includes(i))
        );

        if (availableCarriages.length > 0) {
          selectCarriage(availableCarriages[0].carriageNumber);
        }
      }
    }
  });

  const [animals, { loading: animalsLoading, data: animalsData }] = useLazyQuery(GET_ANIMALS_PER_TRAINS, {
    variables: {
      trainNumbers: [trainNumber],
      departureDate: date
    },
    fetchPolicy: 'network-only'
  });

  const [assistances, { loading: assistancesLoading, data: assistancesData }] = useLazyQuery(GET_ASSISTANCE_PER_TRAINS, {
    variables: {
      trainNumbers: [trainNumber],
      departureDate: date
    },
    fetchPolicy: 'network-only'
  });

  useEffect(
    () => {
      const passengers = products?.map(({ passenger }) => passenger);
      if (passengers?.some(({ type }) => type === types.DOG)) animals();
      if (shouldFetchAssistancesPerCoaches) assistances();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  if (loading || animalsLoading || assistancesLoading || !data) {
    return (
      <Row span={16}>
        <Column justifyContent="center" alignItems="center">
          <Spinner />
        </Column>
      </Row>
    );
  }

  if (error || !data) {
    return null;
  }

  const { carriages: carriageData } = data.getSeatingData;
  const carriages = carriageData
    .slice(0)
    .sort((a, b) => Number(a.sequenceNumber) - Number(b.sequenceNumber))
    .reverse();

  return (
    <StyledTrainMap className={className} count={carriages.length}>
      <div className="tail">
        <ChevronRight color="#bbb" size={12} />
      </div>
      {carriages.map(({ carriageNumber, seats, blocked, blockedReason, inventoryClasses }) => {
        const numberOfAnimals = animalsData?.getAnimalsPerTrains
          ?.find((trains) => trains.trainNumber === trainNumber)
          ?.carriages?.find(({ carriage }) => carriage === parseInt(carriageNumber))?.number;

        const numberOfAssistances = assistancesData?.getAssistancePerTrains
          ?.find((trains) => trains.trainNumber === trainNumber)
          ?.carriages?.find(({ carriage }) => carriage === parseInt(carriageNumber))?.number;

        const carriageMapClassName = getAllowedInventoryClasses(inventoryClasses).reduce(
          (accommodations, inventoryClass) => [
            ...accommodations,
            getClassOfServiceMapNameByInventoryClass(inventoryClass, code, showNewTrainMapName)
          ],
          []
        );

        const isSameInventoryClass = hasSameInventoryClass(currentInventoryClasses, inventoryClasses);

        const inventoryClassesSet = new Set(inventoryClasses);
        const hasWheelchairClass =
          inventoryClassesSet.has(inventoryClassTypes.WHEELCHAIR) || inventoryClassesSet.has(inventoryClassTypes.THWHEELCHAIR);

        const getNumberOfSeatsAvailable = () => {
          const isInventoryClassAvailable = currentInventoryClasses.length > 0;

          if (isInventoryClassAvailable) {
            const filteredInventorySeats = seats.filter((seat) => currentInventoryClasses.includes(seat.inventoryClass));
            const filteredSeats = filteredInventorySeats.filter((seat) => seat.available === false);

            return filteredInventorySeats.length - filteredSeats.length;
          } else {
            const filteredSeats = seats.filter((seat) => seat.available === false);

            return seats.length - filteredSeats.length;
          }
        };

        const handleCarriagePress = () => {
          if (!blocked && isSameInventoryClass) {
            selectCarriage(carriageNumber);
          }
        };

        return (
          <CarriageWrapper key={carriageNumber} color={carriageMapClassName[carriageMapClassName.length - 1]?.color}>
            {numberOfAssistances > 0 && (
              <span>
                <StyledAssistanceNumber value={numberOfAssistances}>{numberOfAssistances}</StyledAssistanceNumber>
                <AssistanceIcon value={numberOfAssistances} />
              </span>
            )}
            {!blocked && isSameInventoryClass && <StyledNumberOfAvailableSeats>{getNumberOfSeatsAvailable()}</StyledNumberOfAvailableSeats>}
            {numberOfAnimals > 0 && (
              <span>
                <AnimalsIcon />
                <StyledAnimalsNumber value={numberOfAnimals}>{numberOfAnimals}</StyledAnimalsNumber>
              </span>
            )}

            <Carriage
              id={`carriage-${carriageNumber}`}
              selected={currentCoach === carriageNumber}
              sameInventoryClass={!blocked && isSameInventoryClass}
              onClick={handleCarriagePress}
            >
              {isSameInventoryClass && blocked && CARRIAGE_BLOCKED_REASONS.includes(blockedReason) && (
                <StyledCarriageBlockedTooltip>
                  <FormattedMessage id="seating.blockedCoachTooltipMessage" />
                </StyledCarriageBlockedTooltip>
              )}

              <StyledCarriageNumber>{carriageNumber.padStart(2, '0')}</StyledCarriageNumber>
              <StyledCarriageFeatures>
                {hasWheelchairClass ? <StyledCarriageWheelchairIcon /> : <span />}
                <StyledCarriageClass>
                  {carriageMapClassName
                    .filter((item) => item)
                    ?.map(({ name }) => name)
                    .join(' | ')}
                </StyledCarriageClass>
              </StyledCarriageFeatures>
            </Carriage>
          </CarriageWrapper>
        );
      })}
      <div className="head">
        <ChevronRight className="chevron-right" color="#bbb" size={12} />
      </div>
    </StyledTrainMap>
  );
};

TrainMap.propTypes = {
  journey: PropTypes.shape({
    trainNumber: PropTypes.string,
    destination: PropTypes.shape({
      uic: PropTypes.string.isRequired
    }),
    origin: PropTypes.shape({
      uic: PropTypes.string.isRequired
    }),
    timing: PropTypes.shape({
      date: PropTypes.string.isRequired
    }),
    classOfService: classOfService,
    serviceType: PropTypes.shape({
      code: PropTypes.string
    }),
    products: PropTypes.arrayOf(
      PropTypes.shape({
        passenger: PropTypes.shape({ type: PropTypes.string })
      })
    )
  }).isRequired,
  className: PropTypes.string,
  selectCarriage: PropTypes.func,
  currentCoach: PropTypes.string,
  nextAvailableTrain: nextAvailableTrainShape,
  currentInventoryClasses: PropTypes.arrayOf(PropTypes.string),
  shouldFetchAssistancesPerCoaches: PropTypes.bool
};

export default TrainMap;
